import React from 'react'
import PropTypes from 'prop-types'
import { FormattedMessage } from 'react-intl'
import { Box, Flex, Copy, Image, HorizontalRule } from '@elparking/components'
import styled from 'styled-components'
import FreeCancel from '../FreeCancel'
import { Ok } from '../Icons'
import { Feature, CarFeatures } from '../CarFeatures'
import DateTime from 'commons/js/ui/DateTime'
import { DAY_OF_MONTH_NUMERIC_FORMAT, LOCALE_TIME_FORMAT, YEAR_MONTH_FORMAT, TRADENAME } from 'commons/js/constants'
import LoadingBlock from 'commons/js/ui/LoadingBlock'
import Cashback from '../Icons/Cashback'
import Gift from '../Icons/Gift'
import NoPayment from '../NoPayment'

const CurrencyWrapperLarge = ({ children }) =>
    <Copy as='h4' padding='0' fontSize='large' fontColor='accent_dark'>
        {children}
    </Copy>

CurrencyWrapperLarge.propTypes = {
    children: PropTypes.node,
}

const Features = ({ features }) => features.map((feature, index) => <Feature
  key={`${index}-card-feature`}
  icon={<Ok fontSize='large' color='accent' />}
  text={feature} />)

Features.propTypes = {
    features: PropTypes.arrayOf(PropTypes.string),
}

Features.defaultProps = {
    iconColor: 'accent_dark',
}

const RentalInfo = ({ date, place, timeZone }) => <>
    <Copy padding='0' transform='capitalize' fontColor='gray90' fontSize={['small', 'xSmall']} fontWeight='regular'>
        <DateTime
          value={date}
          format={DAY_OF_MONTH_NUMERIC_FORMAT}
          timeZone={timeZone}/> <DateTime
            value={date}
            format={YEAR_MONTH_FORMAT} timeZone={timeZone} />{','} <DateTime value={date} format={LOCALE_TIME_FORMAT} timeZone={timeZone}/>
    </Copy>
    <Copy padding='0' fontColor='gray90' fontSize={['small', 'xSmall']} fontWeight='regular'>
        {place.stationName}
    </Copy>
</>

RentalInfo.propTypes = {
    date: PropTypes.number,
    place: PropTypes.shape({
        stationName: PropTypes.string,
    }),
    timeZone: PropTypes.string,
}

const Loader = styled(LoadingBlock)`
    height: 500px;
`

const ExtraInfoContainer = styled(Box)`
    border-radius: 4px;
`
const Resume = ({
    startDate,
    endDate,
    priceDetails,
    originPOI,
    destinationPOI,
    rate,
    loading,
    isMutuaUser,
    buildBookingLoading,
    additionalInfoUrl,
}) => {
    if (!rate || buildBookingLoading) {
        return <Loader color='gray40' size='large' width='100%'/>
    }
    return <Box width='100%'>
        <Flex flexDirection='column' width='100%'>
            <Copy as='h2' padding='0' fontColor='accent_dark' fontSize='large' fontWeight='medium'>
                <FormattedMessage defaultMessage='Tu selección...' />
            </Copy>
            <Flex width='120%' gridGap='10px' mt='10px' mb='10px'>
                {rate.cancelFree && <FreeCancel />}
                <NoPayment />
            </Flex>
            <Box width='100%'>
                <Flex width='100%' alignItems='center' mt='0.5em'>
                    <Box width='120px' pL='10px'>
                        <Image maxWidth='100%' width='140px' height='auto' alt={rate.seller.name} src={rate.car.image} />
                    </Box>
                    <Box width='calc(100% - 135px)' marginLeft='15px'>
                        <Copy as='h3' padding='0' fontColor='accent_dark' fontSize='xLarge' fontWeight='medium'>
                            {rate.car.type}
                        </Copy>
                        <Copy as='h4' padding='0' fontSize={['medium', 'small']} fontWeight='regular' fontColor='gray80'>
                            {rate.car.description}
                        </Copy>
                        <Flex width='100%' mt='5px' mb='5px' alignItems='center'>
                            <Image height='32px' alt={rate.seller.name} src={rate.seller.image} />
                        </Flex>
                    </Box>
                </Flex>
            </Box>
            <Box w='100%' mt='1em'>
                <CarFeatures {...rate.car.features} iconColor='accent' resume />
            </Box>
            <Box mt='0.5em' mb='1em'>
                <Features features={rate.insuranceConfigs} />
            </Box>
            <HorizontalRule margin='0' />
            <Box mt='1em' mb='1em'>
                <Flex width='100%'>
                    <Box width='calc(50% - 2px)'>
                        <Copy padding='0 0 0.4em 0' fontColor='darkGray' fontSize={['small', 'xSmall']} fontWeight='bold'>
                            <FormattedMessage defaultMessage='Recogida' />
                        </Copy>
                        <RentalInfo date={startDate} place={originPOI} timeZone={rate.timeZone} />
                    </Box>
                    <Box w='17px'>
                        <HorizontalRule width='1px' height='100%' margin='0' />
                    </Box>
                    <Box width='calc(50% - 15px)'>
                        <Copy padding='0 0 0.4em 0' fontColor='black' fontSize={['small', 'xSmall']} fontWeight='bold'>
                            <FormattedMessage defaultMessage='Devolución' />
                        </Copy>
                        <RentalInfo date={endDate} place={destinationPOI} timeZone={rate.timeZone}/>
                    </Box>
                </Flex>
            </Box>
            <HorizontalRule margin='0' />
            <Box mt='1em'>
                <Copy padding='0' fontColor='darkGray' fontSize={['small', 'xSmall']} fontWeight='bold'>
                    <FormattedMessage defaultMessage='Detalles del precio' />
                </Copy>
                <Box mt='0.4em'>
                    {priceDetails
                            .sort(({ qty: qtyA }, { qty: qtyB }) => qtyA.amount < qtyB.amount ? 1 : -1)
                            .map(({ title, qty, displayPrice }, index) => {
                                const fontSize = ['small', 'xSmall']
                                return <Box mt='0.3em' key={`concept_${index}`} w='100%'>
                                    <Flex width='100%' justifyContent='space-between'>
                                        <Copy fontColor='gray90' padding='0' fontSize={fontSize} fontWeight={'medium'}>
                                            {title}
                                        </Copy>
                                        <Copy fontColor='gray90' padding='0 0 0 1em' transform='uppercase' fontSize={fontSize} fontWeight={'medium'}>
                                            {qty.amount === 0
                                                ? <FormattedMessage defaultMessage='Gratis' />
                                                : displayPrice
                                            }
                                        </Copy>
                                    </Flex>
                                </Box>
                            })
                        }
                    {loading && <Box mt='0.5em' w='100%'><LoadingBlock size='xLarge' width='100%' /></Box>}
                </Box>
            </Box>
            <Box mt='1em' mb='1.5em'>
                <Flex width='100%' justifyContent='space-between' alignItems='end'>
                    <Box width='100%'>
                        <Copy as='h3' padding='0' fontColor={'accent_dark'} fontSize='xLarge' fontWeight='bold'>
                            <FormattedMessage defaultMessage='Total' />
                        </Copy>
                        <Copy as='span' padding='0' fontColor={'accent_dark'} fontSize='xxxSmall' fontWeight='regular'>
                            <FormattedMessage defaultMessage='(impuestos incluídos)' />
                        </Copy>
                    </Box>
                    {loading
                            ? <LoadingBlock size='xxxLarge' width='100%' />
                            : <Copy width='100%' textAlign='right' padding='0' fontColor='accent_dark' fontSize={['xxLarge', 'xxxLarge']} fontWeight='bold'>
                                <CurrencyWrapperLarge>{rate.displayPrice}</CurrencyWrapperLarge>
                            </Copy>
                        }
                </Flex>

                {rate && rate.displayDepositAmount &&
                <Copy padding='0' fontColor='gray' fontSize='xxxSmall' fontWeight='regular' mt='1em'>
                    <FormattedMessage defaultMessage='*Como garantía, se le retendrá un depósito de {displayDepositAmount}' values={{
                        displayDepositAmount: rate.displayDepositAmount,
                    }}/>
                </Copy>
                    }
            </Box>
            {isMutuaUser && <ExtraInfoContainer mt='0.5em' backgroundColor='accent10' padding='0.5em'>
                <Flex width='100%' alignItems='center'>
                    <Box w='30px'><Gift color='accent' height='32px'/></Box>
                    <Copy padding='0em 0em 0em 0.5em' fontSize={['small', 'xSmall']} lineHeight='small' fontWeight='bold'>
                        <FormattedMessage defaultMessage='Precio exclusivo {reason}' values={{
                            reason: <Copy as='span' fontWeight='regular'><FormattedMessage defaultMessage='por ser de {tradename}' values={{tradename: TRADENAME}} /></Copy>,
                        }}/>
                    </Copy>
                </Flex>
            </ExtraInfoContainer>}
            <ExtraInfoContainer mt='0.5em' backgroundColor='accent10' padding='0.5em'>
                <Flex width='100%' alignItems='center'>
                    <Box w='30px'><Cashback color='accent' height='32px'/></Box>
                    <Flex flexDirection='column' padding='0em 0em 0em 0.5em'>
                        <Copy padding='0' fontSize={['small', 'xSmall']} lineHeight='small' fontWeight='bold'>
                            <FormattedMessage defaultMessage='Bonus del 4% en este alquiler de coche.' />
                        </Copy>
                        <Copy padding='0' fontSize={['small', 'xSmall']} lineHeight='small' fontWeight='regular'>
                            <FormattedMessage defaultMessage='Para no mutualistas te devolvemos un 2%.' />
                        </Copy>
                    </Flex>
                </Flex>
            </ExtraInfoContainer>
        </Flex>
    </Box>
}

Resume.propTypes = {
    id: PropTypes.any,
    rate: PropTypes.object,
    priceDetails: PropTypes.arrayOf(PropTypes.shape({
        title: PropTypes.string,
        qty: PropTypes.object,
        displayPrice: PropTypes.string,
    })),
    features: PropTypes.arrayOf(PropTypes.string),
    startDate: PropTypes.number,
    endDate: PropTypes.number,
    price: PropTypes.shape({
        amount: PropTypes.number,
        currency: PropTypes.string,
    }),
    originPOI: PropTypes.shape({
        name: PropTypes.string,
    }),
    destinationPOI: PropTypes.shape({
        name: PropTypes.string,
    }),
    loading: PropTypes.bool,
    isMutuaUser: PropTypes.bool,
    buildBookingLoading: PropTypes.bool,
    additionalInfoUrl: PropTypes.string,
}

Resume.defaultProps = {
    onClick: () => {},
    isMutuaUser: false,
}

export default Resume
